import { useEffect, useMemo, useState, type FormEvent } from "react";
import { ArrowLeft, FileText, Link2, Paperclip, Pencil, Plus } from "lucide-react";
import { actions, listBudgets, listBusinessUnits, listLocations, listApprovalRules, listRequisitions, listEligibleRequisitionApprovers, requisitionBriefWorkspace } from "@/lib/api";
import type { ApprovalRule, Requisition, TeamMember } from "@/lib/types";
import { displayDate, money, titleCase } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { RequisitionIntelligence } from "@/requisition-intelligence";
import { GuidedBuyingDialog } from "@/guided-buying";
import { useData, matches, reportActionError, type ViewProps, type DialogProps } from "@/lib/app-view";
import { PageHeader } from "@/components/page-header";
import { Empty, Loading, ErrorState } from "@/components/empty-state";
import { Table } from "@/components/data-table";
import { FieldError } from "@/components/field-error";
import { AppLink } from "@/components/app-link";
import { Panel } from "@/components/panel";
import { ControlPassportPanel } from "@/components/control-passport-panel";
import { RequisitionClarificationThread } from "@/components/requisition-clarification-thread";

function requiredApproverCount(
  rules: ApprovalRule[] | undefined,
  value: number,
  category?: string | null,
) {
  const normalizedCategory = category?.trim().toLowerCase();
  const matching = (rules || []).filter(
    (rule) =>
      rule.active &&
      rule.min_value <= value &&
      (rule.max_value === null || rule.max_value >= value) &&
      (!rule.category ||
        rule.category === "*" ||
        rule.category.toLowerCase() === normalizedCategory),
  );
  return matching.length
    ? Math.max(...matching.map((rule) => rule.required_approvals))
    : 1;
}

function eligibleApprovers(team: TeamMember[] | undefined) {
  return (team || []).filter(
    (member) =>
      member.status === "active" &&
      ["approver", "tenant_admin"].includes(member.role),
  );
}

export function Requisitions({
  organisation,
  refresh,
  query,
  recordId,
  can,
  canRequest,
  currentUserId,
  changed,
  navigate,
}: ViewProps & {
  recordId: string;
  navigate: (view: string, recordId?: string) => void;
  canRequest: boolean;
  currentUserId: string;
}) {
  const { data, error, loading } = useData(
    () => listRequisitions(organisation.id),
    [organisation.id, refresh],
  );
  const [creating, setCreating] = useState(false);
  const [briefId, setBriefId] = useState<string>();
  const [guidedId, setGuidedId] = useState<string>();
  const [editing, setEditing] = useState<Requisition>();
  const [assigning, setAssigning] = useState<Requisition>();
  const [busy, setBusy] = useState("");
  if (loading) return <Loading />;
  if (error || !data) return <ErrorState message={error} />;
  const rows = data.filter((row) =>
    matches(
      query,
      row.id,
      row.request_no,
      row.title,
      row.status,
      row.profiles?.full_name,
      row.locations?.name,
    ),
  );
  async function submit(id: string) {
    setBusy(id);
    try {
      await actions.submitRequisition(organisation.id, id);
      changed();
    } catch (reason) {
      reportActionError(reason);
    } finally {
      setBusy("");
    }
  }
  if (recordId) {
    const requisition = data.find((row) => row.id === recordId);
    if (!requisition)
      return (
        <>
          <PageHeader
            eyebrow="SOURCE REQUEST"
            title="Requisition unavailable"
            description="This record is not available in the current organisation."
          >
            <Button variant="outline" asChild>
              <AppLink view="requisitions" navigate={navigate}>
                <ArrowLeft className="size-4" aria-hidden="true" />
                All requisitions
              </AppLink>
            </Button>
          </PageHeader>
        </>
      );
    return (
      <>
        <RequisitionRecord
          row={requisition}
          organisationId={organisation.id}
          can={can}
          currentUserId={currentUserId}
          changed={changed}
          navigate={navigate}
          actions={
            <RequisitionActions
              row={requisition}
              busy={busy}
              can={can}
              canRequest={canRequest}
              currentUserId={currentUserId}
              onBrief={setBriefId}
              onGuided={setGuidedId}
              onSubmit={submit}
              onAssign={setAssigning}
              onEdit={setEditing}
            />
          }
        />
        <RequisitionDialog
          open={Boolean(editing)}
          onOpenChange={(open) => !open && setEditing(undefined)}
          organisationId={organisation.id}
          requisition={editing}
          onDone={changed}
        />
        <ApproverAssignmentDialog
          requisition={assigning}
          open={Boolean(assigning)}
          onOpenChange={(open) => !open && setAssigning(undefined)}
          organisationId={organisation.id}
          onDone={changed}
        />
        <RequisitionIntelligence
          organisationId={organisation.id}
          requisitionId={briefId}
          open={Boolean(briefId)}
          onOpenChange={(open) => !open && setBriefId(undefined)}
          onNavigate={navigate}
        />
        <GuidedBuyingDialog
          organisationId={organisation.id}
          requisitionId={guidedId}
          open={Boolean(guidedId)}
          onOpenChange={(open) => !open && setGuidedId(undefined)}
          canEvaluate={canRequest || can("procure") || can("manage")}
          canReviewException={can("approve")}
          onChanged={changed}
        />
      </>
    );
  }
  return (
    <>
      <PageHeader
        eyebrow="REQUIRE"
        title="Requisitions"
        description="Define the need once, then review its completeness and decision brief before sourcing."
      >
        {canRequest && (
          <Button className="min-h-11" onClick={() => setCreating(true)}>
            <Plus className="size-4" aria-hidden="true" />
            New requisition
          </Button>
        )}
      </PageHeader>
      {rows.length ? (
        <>
          <div className="hidden md:block">
            <Table
              heads={[
                "Request",
                "Requester / location",
                "Need by",
                "Estimate",
                "Status",
                "Action",
              ]}
            >
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>
                    <AppLink
                      view="requisitions"
                      recordId={row.id}
                      navigate={navigate}
                      ariaLabel={`Open ${row.request_no}: ${row.title}`}
                      className="inline-block"
                    >
                      <strong>{row.request_no}</strong>
                      <small className="block text-[var(--text-secondary)]">
                        {row.title} · {row.requisition_lines?.length || 0} lines
                      </small>
                    </AppLink>
                  </td>
                  <td>
                    {row.profiles?.full_name || "User"}
                    <small className="block text-[var(--text-secondary)]">
                      {row.locations?.name}
                    </small>
                    <small className="block text-[var(--text-secondary)]">
                      Approver{(row.requisition_approval_assignments?.length || 0) === 1
                        ? ""
                        : "s"}: {row.requisition_approval_assignments
                        ?.map((assignment) => assignment.approver?.full_name || "Assigned member")
                        .join(", ") || "Not assigned"}
                    </small>
                  </td>
                  <td>{displayDate(row.needed_by)}</td>
                  <td className="numeric">{money(row.estimated_value)}</td>
                  <td>
                    <Badge value={row.status} />
                  </td>
                  <td>
                    <RequisitionActions
                      row={row}
                      busy={busy}
                      can={can}
                      canRequest={canRequest}
                      currentUserId={currentUserId}
                      onBrief={setBriefId}
                      onGuided={setGuidedId}
                      onSubmit={submit}
                      onAssign={setAssigning}
                      onEdit={setEditing}
                    />
                  </td>
                </tr>
              ))}
            </Table>
          </div>
          <div className="grid gap-3 md:hidden" aria-label="Requisitions">
            {rows.map((row) => (
              <article
                key={row.id}
                className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-4 shadow-[var(--shadow-sm)]"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <AppLink
                      view="requisitions"
                      recordId={row.id}
                      navigate={navigate}
                      ariaLabel={`Open ${row.request_no}: ${row.title}`}
                      className="block"
                    >
                      <span className="block text-xs font-bold tracking-[.08em] text-[var(--text-muted)]">
                        {row.request_no}
                      </span>
                      <span className="mt-1 block text-base font-semibold text-[var(--text-primary)]">
                        {row.title}
                      </span>
                    </AppLink>
                  </div>
                  <Badge value={row.status} />
                </div>
                <dl className="mt-4 grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                  <div>
                    <dt className="text-xs text-[var(--text-muted)]">Estimate</dt>
                    <dd className="mt-0.5 font-semibold tabular-nums">
                      {money(row.estimated_value)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs text-[var(--text-muted)]">Need by</dt>
                    <dd className="mt-0.5">{displayDate(row.needed_by)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-[var(--text-muted)]">Requester</dt>
                    <dd className="mt-0.5">{row.profiles?.full_name || "User"}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-[var(--text-muted)]">Location</dt>
                    <dd className="mt-0.5">{row.locations?.name || "Not set"}</dd>
                  </div>
                  <div className="col-span-2">
                    <dt className="text-xs text-[var(--text-muted)]">Approval route</dt>
                    <dd className="mt-0.5">
                      {row.requisition_approval_assignments
                        ?.map((assignment) => assignment.approver?.full_name || "Assigned member")
                        .join(", ") || "Not assigned"}
                    </dd>
                  </div>
                </dl>
                <div className="mt-4 border-t border-[var(--border)] pt-4">
                  <RequisitionActions
                    row={row}
                    busy={busy}
                    can={can}
                    canRequest={canRequest}
                    currentUserId={currentUserId}
                    onBrief={setBriefId}
                    onGuided={setGuidedId}
                    onSubmit={submit}
                    onAssign={setAssigning}
                    onEdit={setEditing}
                    mobile
                  />
                </div>
              </article>
            ))}
          </div>
        </>
      ) : (
        <Empty
          title="No requisitions yet"
          description="Create the first procurement request for this workspace."
          action={
            canRequest && (
              <Button onClick={() => setCreating(true)}>New requisition</Button>
            )
          }
        />
      )}
      <RequisitionDialog
        open={creating}
        onOpenChange={setCreating}
        organisationId={organisation.id}
        onDone={changed}
      />
      <RequisitionDialog
        open={Boolean(editing)}
        onOpenChange={(open) => !open && setEditing(undefined)}
        organisationId={organisation.id}
        requisition={editing}
        onDone={changed}
      />
      <ApproverAssignmentDialog
        requisition={assigning}
        open={Boolean(assigning)}
        onOpenChange={(open) => !open && setAssigning(undefined)}
        organisationId={organisation.id}
        onDone={changed}
      />
      <RequisitionIntelligence
        organisationId={organisation.id}
        requisitionId={briefId}
        open={Boolean(briefId)}
        onOpenChange={(open) => !open && setBriefId(undefined)}
        onNavigate={navigate}
      />
      <GuidedBuyingDialog
        organisationId={organisation.id}
        requisitionId={guidedId}
        open={Boolean(guidedId)}
        onOpenChange={(open) => !open && setGuidedId(undefined)}
        canEvaluate={canRequest || can("procure") || can("manage")}
        canReviewException={can("approve")}
        onChanged={changed}
      />
    </>
  );
}

function RequisitionRecord({
  row,
  organisationId,
  can,
  currentUserId,
  changed,
  navigate,
  actions: recordActions,
}: {
  row: Requisition;
  organisationId: string;
  can: ViewProps["can"];
  currentUserId: string;
  changed: () => void;
  navigate: (view: string, recordId?: string) => void;
  actions: React.ReactNode;
}) {
  const { data, error, loading } = useData(
    () => requisitionBriefWorkspace(organisationId, row.id),
    [organisationId, row.id],
  );
  return (
    <>
      <PageHeader
        eyebrow="SOURCE REQUEST"
        title={`${row.request_no} · ${row.title}`}
        description={
          row.description || "No business-need narrative is recorded for this requisition."
        }
      >
        <Button variant="outline" asChild>
          <AppLink view="requisitions" navigate={navigate}>
            <ArrowLeft className="size-4" aria-hidden="true" />
            All requisitions
          </AppLink>
        </Button>
      </PageHeader>

      <div className="mb-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[var(--border)] bg-[var(--surface)] p-4">
        <div className="flex items-center gap-3">
          <Badge value={row.status} />
          <span className="text-sm text-[var(--text-secondary)]">
            Source facts and governed actions
          </span>
        </div>
        {recordActions}
      </div>

      <ControlPassportPanel
        organisationId={organisationId}
        checkpoint="requisition"
        requestNo={row.request_no}
        viewerRole={
          can("manage") ? "tenant administrator" :
          can("approve") ? "approver" :
          can("procure") ? "procurement" :
          can("finance") ? "finance" :
          can("receive") ? "receiver" : "requester"
        }
        capabilities={{
          manage: can("manage"),
          approve: can("approve"),
          procure: can("procure"),
          finance: can("finance"),
          receive: can("receive"),
        }}
        onNavigate={navigate}
      />

      <RequisitionClarificationThread
        organisationId={organisationId}
        requisitionId={row.id}
        canRespond={row.created_by === currentUserId || can("manage")}
        onChanged={changed}
      />

      <div className="grid gap-5 xl:grid-cols-[.9fr_1.1fr]">
        <div className="grid content-start gap-5">
          <Panel>
            <p className="eyebrow">REQUEST FACTS</p>
            <h2 className="mt-1 font-serif text-xl">Requirement</h2>
            <dl className="mt-4 grid grid-cols-2 gap-4 text-sm">
              <RecordFact term="Requester" value={row.profiles?.full_name || "Not recorded"} />
              <RecordFact term="Delivery location" value={row.locations?.name || "Not recorded"} />
              <RecordFact term="Needed by" value={displayDate(row.needed_by)} />
              <RecordFact term="Estimated value" value={money(row.estimated_value)} numeric />
              <RecordFact term="Created" value={displayDate(row.created_at, true)} />
              <RecordFact term="Request status" value={titleCase(row.status)} />
            </dl>
          </Panel>

          <Panel>
            <p className="eyebrow">APPROVAL ROUTE</p>
            <h2 className="mt-1 font-serif text-xl">Assigned decision makers</h2>
            <div className="mt-4 grid gap-2">
              {row.requisition_approval_assignments?.length ? (
                row.requisition_approval_assignments.map((assignment) => (
                  <div
                    key={assignment.id}
                    className="flex items-center justify-between gap-3 rounded-lg border border-[var(--border)] p-3 text-sm"
                  >
                    <span>
                      {assignment.position}. {assignment.approver?.full_name || "Assigned member"}
                    </span>
                    <span className="text-xs text-[var(--text-muted)]">
                      {assignment.reassignment_required_at ? "Reassignment required" : "Assigned"}
                    </span>
                  </div>
                ))
              ) : (
                <p className="text-sm text-[var(--text-secondary)]">No approver is assigned.</p>
              )}
            </div>
          </Panel>
        </div>

        <div className="grid content-start gap-5">
          <Panel>
            <p className="eyebrow">REQUESTED LINES</p>
            <h2 className="mt-1 font-serif text-xl">Items and specifications</h2>
            <div className="mt-4 divide-y divide-[var(--border)] rounded-xl border border-[var(--border)]">
              {(row.requisition_lines || []).map((line) => (
                <article className="grid gap-3 p-4 sm:grid-cols-[1fr_auto]" key={line.id}>
                  <div>
                    <strong className="text-sm">{line.name}</strong>
                    <p className="mt-1 text-sm text-[var(--text-secondary)]">
                      {line.specification}
                    </p>
                  </div>
                  <div className="text-left text-sm sm:text-right">
                    <strong className="tabular-nums">
                      {line.quantity} {line.unit}
                    </strong>
                    <span className="mt-1 block text-xs tabular-nums text-[var(--text-secondary)]">
                      {money(Number(line.quantity) * Number(line.estimated_unit_price))}
                    </span>
                  </div>
                </article>
              ))}
            </div>
          </Panel>

          <Panel>
            <p className="eyebrow">GOVERNED EVIDENCE</p>
            <h2 className="mt-1 font-serif text-xl">Decisions and linked records</h2>
            {loading && (
              <p className="mt-4 text-sm text-[var(--text-secondary)]">Loading linked tenant records…</p>
            )}
            {error && (
              <p className="mt-4 text-sm text-[var(--error)]">
                Linked evidence is temporarily unavailable: {error}
              </p>
            )}
            {!loading && !error && data && (
              <div className="mt-4 grid gap-4 text-sm">
                <EvidenceLine icon={<FileText className="size-4" aria-hidden="true" />} label="Approval decisions">
                  {data.approvals.length
                    ? data.approvals
                        .map((approval: any) => `${approval.profiles?.full_name || "Approver"}: ${titleCase(approval.decision)}`)
                        .join(" · ")
                    : "None recorded"}
                </EvidenceLine>
                <EvidenceLine icon={<Paperclip className="size-4" aria-hidden="true" />} label="Attachments">
                  {data.attachments.length
                    ? data.attachments.map((attachment: any) => attachment.file_name).join(", ")
                    : "None recorded"}
                </EvidenceLine>
                <EvidenceLine icon={<Link2 className="size-4" aria-hidden="true" />} label="Downstream records">
                  {data.rfq
                    ? `${data.rfq.rfq_no}${data.purchaseOrder ? ` · ${data.purchaseOrder.po_no}` : ""}`
                    : "Sourcing has not started"}
                </EvidenceLine>
              </div>
            )}
          </Panel>
        </div>
      </div>
    </>
  );
}

function RecordFact({ term, value, numeric = false }: { term: string; value: string; numeric?: boolean }) {
  return (
    <div>
      <dt className="text-xs text-[var(--text-muted)]">{term}</dt>
      <dd className={`mt-1 font-medium ${numeric ? "tabular-nums" : ""}`}>{value}</dd>
    </div>
  );
}

function EvidenceLine({ icon, label, children }: { icon: React.ReactNode; label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-[auto_1fr] gap-3 rounded-lg border border-[var(--border)] p-3">
      <span className="mt-0.5 text-[var(--teal)]">{icon}</span>
      <div>
        <strong>{label}</strong>
        <p className="mt-1 text-[var(--text-secondary)]">{children}</p>
      </div>
    </div>
  );
}

function RequisitionActions({
  row,
  busy,
  can,
  canRequest,
  currentUserId,
  onBrief,
  onGuided,
  onSubmit,
  onAssign,
  onEdit,
  mobile = false,
}: {
  row: Requisition;
  busy: string;
  can: ViewProps["can"];
  canRequest: boolean;
  currentUserId: string;
  onBrief: (id: string) => void;
  onGuided: (id: string) => void;
  onSubmit: (id: string) => void;
  onAssign: (row: Requisition) => void;
  onEdit: (row: Requisition) => void;
  mobile?: boolean;
}) {
  const ownsDraft = row.created_by === currentUserId || can("manage");
  const mayReviewDraft =
    row.created_by === currentUserId ||
    can("approve") ||
    can("procure") ||
    can("manage");
  const guidedReviewComplete = [
    "clear",
    "warning_acknowledged",
    "exception_approved",
  ].includes(row.guided_buying_state || "");
  const maySubmit =
    row.status === "draft" && canRequest && ownsDraft && guidedReviewComplete;

  return (
    <div className={`flex flex-wrap gap-2 ${mobile ? "[&>button]:min-h-11 [&>button]:flex-1" : ""}`}>
      <Button size="sm" variant="outline" onClick={() => onBrief(row.id)}>
        Review brief
      </Button>
      {row.status === "draft" && mayReviewDraft && (
        <Button size="sm" variant="outline" onClick={() => onGuided(row.id)}>
          Guided review
        </Button>
      )}
      {row.status === "draft" && ownsDraft && (
        <Button size="sm" variant="outline" onClick={() => onEdit(row)}>
          <Pencil className="size-3.5" aria-hidden="true" />
          Edit draft
        </Button>
      )}
      {maySubmit ? (
        <Button
          size="sm"
          disabled={busy === row.id}
          onClick={() => onSubmit(row.id)}
        >
          {busy === row.id ? "Submitting…" : "Submit"}
        </Button>
      ) : (
        <span className="self-center text-xs text-[var(--text-muted)]">
          {row.status === "draft" && canRequest && ownsDraft && !guidedReviewComplete
            ? "Guided review required"
            : "No action due"}
        </span>
      )}
      {row.status === "draft" && ownsDraft && (
        <Button size="sm" variant="outline" onClick={() => onAssign(row)}>
          {row.requisition_approval_assignments?.length
            ? "Change approvers"
            : "Assign approvers"}
        </Button>
      )}
    </div>
  );
}

function RequisitionDialog({
  open,
  onOpenChange,
  organisationId,
  onDone,
  requisition,
}: DialogProps & { requisition?: Requisition }) {
  const { data: locations } = useData(
    () => listLocations(organisationId),
    [organisationId, open],
  );
  const { data: units } = useData(
    () => listBusinessUnits(organisationId),
    [organisationId, open],
  );
  const { data: budgets } = useData(
    () => listBudgets(organisationId),
    [organisationId, open],
  );
  const { data: team } = useData(
    () => listEligibleRequisitionApprovers(organisationId),
    [organisationId, open],
  );
  const { data: approvalRules } = useData(
    () => listApprovalRules(organisationId),
    [organisationId, open],
  );
  const [lines, setLines] = useState([
    {
      name: "",
      specification: "",
      quantity: "1",
      unit: "Nos",
      estimated_unit_price: "",
    },
  ]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [unitId, setUnitId] = useState("");
  const [budgetId, setBudgetId] = useState("");
  const [approverIds, setApproverIds] = useState<string[]>([]);
  const editing = Boolean(requisition);

  useEffect(() => {
    if (!open) return;
    setLines(
      requisition?.requisition_lines?.length
        ? requisition.requisition_lines.map((line) => ({
            name: line.name,
            specification: line.specification,
            quantity: String(line.quantity),
            unit: line.unit,
            estimated_unit_price: String(line.estimated_unit_price),
          }))
        : [
            {
              name: "",
              specification: "",
              quantity: "1",
              unit: "Nos",
              estimated_unit_price: "",
            },
          ],
    );
    setApproverIds(
      requisition?.requisition_approval_assignments
        ?.slice()
        .sort((a, b) => a.position - b.position)
        .map((assignment) => assignment.approver_id) || [],
    );
    setUnitId(requisition?.business_unit_id || "");
    setBudgetId(requisition?.budget_id || "");
    setError("");
  }, [open, requisition]);
  const estimatedValue = useMemo(
    () =>
      lines.reduce(
        (sum, line) =>
          sum +
          Number(line.quantity || 0) * Number(line.estimated_unit_price || 0),
        0,
      ),
    [lines],
  );
  const requiredApprovals = requiredApproverCount(
    approvalRules,
    estimatedValue,
    requisition?.cost_category ||
      budgets?.find((budget) => budget.id === budgetId)?.cost_category ||
      "General",
  );
  const approvers = eligibleApprovers(team);
  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    if (lines.some((line) => line.estimated_unit_price === "")) {
      setError(
        "Confirm the estimated unit price for every line. Enter 0 only when zero is the intended recorded estimate.",
      );
      return;
    }
    if (approverIds.length !== requiredApprovals) {
      setError(
        `Select exactly ${requiredApprovals} approver${requiredApprovals === 1 ? "" : "s"} for this request value.`,
      );
      return;
    }
    setBusy(true);
    const form = new FormData(event.currentTarget);
    const payload = {
      p_title: form.get("title"),
      p_description: form.get("description"),
      p_location_id: form.get("location"),
      p_needed_by: form.get("neededBy"),
      p_approver_ids: approverIds,
      p_lines: lines.map((line) => ({
        ...line,
        quantity: Number(line.quantity),
        estimated_unit_price: Number(line.estimated_unit_price),
      })),
    };
    try {
      if (requisition) {
        await actions.updateRequisitionDraft(
          organisationId,
          requisition.id,
          payload,
        );
      } else {
        const selectedBudgetId = String(form.get("budget") || "");
        if (selectedBudgetId)
        await actions.createBudgetedRequisition(organisationId, {
          ...payload,
          p_business_unit_id: form.get("businessUnit"),
          p_budget_id: selectedBudgetId,
          p_cost_category:
            budgets?.find((b) => b.id === selectedBudgetId)?.cost_category || "General",
        });
        else await actions.createRequisition(organisationId, payload);
      }
      onOpenChange(false);
      onDone();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }
  const editLine = (index: number, field: string, value: string) =>
    setLines((current) =>
      current.map((line, lineIndex) =>
        lineIndex === index ? { ...line, [field]: value } : line,
      ),
    );
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader
          eyebrow={editing ? "DRAFT REQUEST" : "NEW REQUEST"}
          title={editing ? `Edit ${requisition?.request_no}` : "Create requisition"}
          description={
            editing
              ? "Update the source facts before submission. Guided Buying will require a fresh review."
              : "Save a complete, procurement-ready draft."
          }
        />
        <form onSubmit={save} className="grid gap-4 p-6">
          <div className="grid gap-4 md:grid-cols-2">
            <Label>
              Request title
              <Input name="title" defaultValue={requisition?.title || ""} required />
            </Label>
            <Label>
              Needed by
              <Input
                name="neededBy"
                type="date"
                defaultValue={requisition?.needed_by || ""}
                required
              />
            </Label>
            <Label>
              Delivery location
              <Select name="location" defaultValue={requisition?.location_id || ""} required>
                {locations?.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </Select>
            </Label>
            {!editing && (
              <>
                <Label>
                  Operating unit (optional)
                  <Select
                    name="businessUnit"
                    value={unitId}
                    onChange={(event) => setUnitId(event.target.value)}
                  >
                    <option value="">Unallocated</option>
                    {units?.map((unit) => (
                      <option key={unit.id} value={unit.id}>
                        {unit.code} · {unit.name}
                      </option>
                    ))}
                  </Select>
                </Label>
                <Label>
                  Budget reservation (optional)
                  <Select
                    name="budget"
                    value={budgetId}
                    onChange={(event) => setBudgetId(event.target.value)}
                  >
                    <option value="">No budget reservation</option>
                    {budgets
                      ?.filter(
                        (b) =>
                          b.status === "active" &&
                          (!unitId || b.business_unit_id === unitId),
                      )
                      .map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.cost_category} · FY {b.fiscal_year} ·{" "}
                          {money(b.allocated_amount, b.currency)}
                        </option>
                      ))}
                  </Select>
                </Label>
              </>
            )}
          </div>
          {editing && (requisition?.budget_id || requisition?.business_unit_id) && (
            <p className="rounded-lg bg-[var(--surface-muted)] p-3 text-sm text-[var(--text-secondary)]">
              The existing operating-unit and budget allocation remain unchanged by this edit.
            </p>
          )}
          <Label>
            Business need (required)
            <Textarea
              name="description"
              defaultValue={requisition?.description || ""}
              required
            />
          </Label>
          <div className="rounded-xl bg-[var(--surface-muted)] p-4">
            <div className="mb-3 flex items-center justify-between gap-3">
              <strong>Line items</strong>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() =>
                  setLines((current) => [
                    ...current,
                    {
                      name: "",
                      specification: "",
                      quantity: "1",
                      unit: "Nos",
                      estimated_unit_price: "",
                    },
                  ])
                }
              >
                Add line
              </Button>
            </div>
            {lines.map((line, index) => (
              <div
                key={index}
                className="mb-3 grid gap-2 border-b border-[var(--border)] pb-3 last:border-0 md:grid-cols-[1fr_1.5fr_.5fr_.5fr_.7fr_auto]"
              >
                <Input
                  aria-label="Item name"
                  placeholder="Item"
                  value={line.name}
                  onChange={(event) =>
                    editLine(index, "name", event.target.value)
                  }
                  required
                />
                <Input
                  aria-label="Specification"
                  placeholder="Specification"
                  value={line.specification}
                  onChange={(event) =>
                    editLine(index, "specification", event.target.value)
                  }
                  required
                />
                <Input
                  aria-label="Quantity"
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={line.quantity}
                  onChange={(event) =>
                    editLine(index, "quantity", event.target.value)
                  }
                  required
                />
                <Input
                  aria-label="Unit"
                  value={line.unit}
                  onChange={(event) =>
                    editLine(index, "unit", event.target.value)
                  }
                  required
                />
                <Input
                  aria-label="Estimated unit price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={line.estimated_unit_price}
                  onChange={(event) =>
                    editLine(index, "estimated_unit_price", event.target.value)
                  }
                  required
                />
                {lines.length > 1 && (
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="size-11"
                    aria-label={`Remove line ${index + 1}`}
                    onClick={() =>
                      setLines((current) =>
                        current.filter((_, lineIndex) => lineIndex !== index),
                      )
                    }
                  >
                    ×
                  </Button>
                )}
              </div>
            ))}
          </div>
          <div className="rounded-xl border border-[var(--border)] p-4">
            <div className="mb-3">
              <strong>Who approves this request?</strong>
              <p className="mt-1 text-sm text-[var(--text-secondary)]">
                Select exactly {requiredApprovals} active approver
                {requiredApprovals === 1 ? "" : "s"}. The approval flow begins
                only after the Guided Buying review is complete and the draft is
                submitted.
              </p>
            </div>
            {approvers.length ? (
              <div className="grid gap-2 sm:grid-cols-2">
                {approvers.map((member) => (
                  <label
                    key={member.user_id}
                    className="flex min-h-11 cursor-pointer items-start gap-3 rounded-lg border border-[var(--border)] p-3"
                  >
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={approverIds.includes(member.user_id)}
                      onChange={(event) =>
                        setApproverIds((current) =>
                          event.target.checked
                            ? [...current, member.user_id]
                            : current.filter((id) => id !== member.user_id),
                        )
                      }
                    />
                    <span>
                      <strong className="block text-sm">
                        {member.profiles?.full_name || member.profiles?.email || "Approver"}
                      </strong>
                      <small className="text-[var(--text-secondary)]">
                        {titleCase(member.role)}
                      </small>
                    </span>
                  </label>
                ))}
              </div>
            ) : (
              <p className="text-sm text-[var(--danger)]" role="alert">
                No active approver is available. Ask an organisation administrator
                to add an Approver before creating this request.
              </p>
            )}
          </div>
          <FieldError>{error}</FieldError>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end [&>button]:min-h-11">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button disabled={busy || !approvers.length} type="submit">
              {busy ? "Saving…" : editing ? "Save changes" : "Save draft"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ApproverAssignmentDialog({
  requisition,
  open,
  onOpenChange,
  organisationId,
  onDone,
}: DialogProps & { requisition?: Requisition }) {
  const { data: team } = useData(
    () => listEligibleRequisitionApprovers(organisationId),
    [organisationId, open],
  );
  const { data: approvalRules } = useData(
    () => listApprovalRules(organisationId),
    [organisationId, open],
  );
  const [approverIds, setApproverIds] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const requiredApprovals = requiredApproverCount(
    approvalRules,
    Number(requisition?.estimated_value || 0),
    requisition?.cost_category,
  );
  const approvers = eligibleApprovers(team);

  useEffect(() => {
    if (!open) return;
    setApproverIds(
      requisition?.requisition_approval_assignments?.map(
        (assignment) => assignment.approver_id,
      ) || [],
    );
    setError("");
  }, [open, requisition]);

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!requisition) return;
    if (approverIds.length !== requiredApprovals) {
      setError(
        `Select exactly ${requiredApprovals} approver${requiredApprovals === 1 ? "" : "s"}.`,
      );
      return;
    }
    setBusy(true);
    setError("");
    try {
      await actions.setRequisitionApprovers(
        organisationId,
        requisition.id,
        approverIds,
      );
      onOpenChange(false);
      onDone();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader
          eyebrow="APPROVAL ROUTE"
          title="Assign request approvers"
          description={`This request requires ${requiredApprovals} recorded approval${requiredApprovals === 1 ? "" : "s"}.`}
        />
        <form onSubmit={save} className="grid gap-4 p-6">
          {approvers.length ? (
            <div className="grid gap-2 sm:grid-cols-2">
              {approvers.map((member) => (
                <label
                  key={member.user_id}
                  className="flex min-h-11 cursor-pointer items-start gap-3 rounded-lg border border-[var(--border)] p-3"
                >
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={approverIds.includes(member.user_id)}
                    onChange={(event) =>
                      setApproverIds((current) =>
                        event.target.checked
                          ? [...current, member.user_id]
                          : current.filter((id) => id !== member.user_id),
                      )
                    }
                  />
                  <span>
                    <strong className="block text-sm">
                      {member.profiles?.full_name || member.profiles?.email || "Approver"}
                    </strong>
                    <small className="text-[var(--text-secondary)]">
                      {titleCase(member.role)}
                    </small>
                  </span>
                </label>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[var(--danger)]" role="alert">
              No active approver is available for this organisation.
            </p>
          )}
          <FieldError>{error}</FieldError>
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end [&>button]:min-h-11">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={busy || !approvers.length}>
              {busy ? "Saving…" : "Save approval route"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
